import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiClient } from "../../../../../api/httpClient";
import logger from "../../../../../globals/utils/logger";
import { toast } from "react-toastify";

import {
  CARRIER_PROFILE_HEADER_ROW_SX,
  CARRIER_PROFILE_PAGINATION_SX,
  CARRIER_PROFILE_POSITIONS,
  CARRIER_PROFILE_PRIORITIES,
  CARRIER_PROFILE_STATUSES,
  CARRIER_PROFILE_TABLE_CARD_SX,
} from "../carrierProfileConfig";

export default function useCarrierProfile() {
  const [open, setOpen] = useState(false);
  const [openBondDialog, setOpenBondDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const {id} = useParams();
  
  const [carrier, setCarrier] = useState('');
  useEffect(()=>{setCarrier(localStorage.getItem("carrierName"))}, [])

  // Tabs
  const [tabValue, setTabValue] = useState(0);
  const [bondGraphData, setBondGraphData] = useState({ labels: [], datasets: [{ label: 'Bond History', backgroundColor: '#1976d2', data: [] }] });

  // Carrier details (view/edit)
  const [carrierDetails, setCarrierDetails] = useState({});
  const [isEditingCarrier, setIsEditingCarrier] = useState(false);
  const [isSavingCarrier, setIsSavingCarrier] = useState(false);

  const fetchCarrierDetails = async () => {
    try {
      const response = await apiClient(`/carriers/${id}`, 'GET');
      const data = response?.data ?? response;
      setCarrierDetails(data || {});
    } catch (err) {
      logger.warn('useCarrierProfile', 'fetchCarrierDetails failed', { err });
    }
  };

  const handleSaveCarrier = async () => {
    try {
      setIsSavingCarrier(true);
      const payload = {
        name: carrierDetails.name || '',
        carrier_processing_email: carrierDetails.carrier_processing_email || '',
        agency_code: carrierDetails.agency_code || '',
      };
      await apiClient(`/carriers/${id}/`, 'PATCH', payload);
      toast.success('Carrier updated successfully');
      setIsEditingCarrier(false);
      fetchCarrierDetails();
    } catch (err) {
      logger.warn('useCarrierProfile', 'handleSaveCarrier failed', { err });
      toast.error('Failed to update carrier');
    } finally {
      setIsSavingCarrier(false);
    }
  };

  // Pagination: Team Members
  const [pageTeam, setPageTeam] = useState(0);
  const [rowsPerPageTeam, setRowsPerPageTeam] = useState(10);
  const handleChangePageTeam = (event, newPage) => setPageTeam(newPage);
  const handleChangeRowsPerPageTeam = (event) => { setRowsPerPageTeam(Number.parseInt(event.target.value, 10)); setPageTeam(0); };

  // Pagination: Companies
  const [pageCompanies, setPageCompanies] = useState(0);
  const [rowsPerPageCompanies, setRowsPerPageCompanies] = useState(10);
  const handleChangePageCompanies = (event, newPage) => setPageCompanies(newPage);
  const handleChangeRowsPerPageCompanies = (event) => { setRowsPerPageCompanies(Number.parseInt(event.target.value, 10)); setPageCompanies(0); };

  // Pagination: Commissions
  const [pageCommissions, setPageCommissions] = useState(0);
  const [rowsPerPageCommissions, setRowsPerPageCommissions] = useState(10);
  const handleChangePageCommissions = (event, newPage) => setPageCommissions(newPage);
  const handleChangeRowsPerPageCommissions = (event) => { setRowsPerPageCommissions(Number.parseInt(event.target.value, 10)); setPageCommissions(0); };

  // Pagination: Limits
  const [pageLimits, setPageLimits] = useState(0);
  const [rowsPerPageLimits, setRowsPerPageLimits] = useState(10);
  const handleChangePageLimits = (event, newPage) => setPageLimits(newPage);
  const handleChangeRowsPerPageLimits = (event) => { setRowsPerPageLimits(Number.parseInt(event.target.value, 10)); setPageLimits(0); };

  // Pagination: States
  const [pageStates, setPageStates] = useState(0);
  const [rowsPerPageStates, setRowsPerPageStates] = useState(10);
  const handleChangePageStates = (event, newPage) => setPageStates(newPage);
  const handleChangeRowsPerPageStates = (event) => { setRowsPerPageStates(Number.parseInt(event.target.value, 10)); setPageStates(0); };

  // Pagination: Bonds
  const [pageBonds, setPageBonds] = useState(0);
  const [rowsPerPageBonds, setRowsPerPageBonds] = useState(10);
  const handleChangePageBonds = (event, newPage) => setPageBonds(newPage);
  const handleChangeRowsPerPageBonds = (event) => { setRowsPerPageBonds(Number.parseInt(event.target.value, 10)); setPageBonds(0); };


  //Team Members
  const [teamMemberName, setTeamMemberName] = useState("");
  const [teamMemberEmail, setTeamMemberEmail] = useState("");
  const [teamMemberPhone, setTeamMemberPhone] = useState("");
  const [teamMemberPosition, setTeamMemberPosition] = useState("");
  const [teamMemberStatus, setTeamMemberStatus] = useState("");
  const [teamMemberPriority, setTeamMemberPriority] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendInvite, setSendInvite] = useState(false);

  const positions = CARRIER_PROFILE_POSITIONS;
  const statuses = CARRIER_PROFILE_STATUSES;
  const priorities = CARRIER_PROFILE_PRIORITIES;

  const [teamMembers, setTeamMembers] = useState([]);

  // Handle dialog open/close
  const handleOpenDialog = () => setOpen(true);
  const handleCloseDialog = () => setOpen(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // Open the edit dialog
  const handleEditClick = (member) => {
    setSelectedMember(member);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedMember(null);
  };

   // Handle the update API call
   const handleUpdateMember = async () => {
    if (!selectedMember) return;
    const { team_member_id, ...updateData } = selectedMember;
    delete updateData.user;

    setIsSubmitting(true);
    try {
      await apiClient(`/carrier-team-member/${team_member_id}`, "PATCH", updateData);
      toast.success("Team member updated successfully!");
      handleCloseEditDialog();
      fetchTeamMembers(); // Refresh the list
    } catch (error) {
      logger.warn("useCarrierProfile", "handleUpdateMember failed", { err: error });
      toast.error("Failed to update team member.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete API call
  const handleDeleteMember = async (team_member_id) => {
    if (!window.confirm("Are you sure you want to delete this team member?")) return;

    setLoading(true);
    try {
      await apiClient(`/carrier-team-member/${team_member_id}`, "DELETE");
      toast.success("Team member deleted successfully!");
      fetchTeamMembers(); // Refresh the list
    } catch (error) {
      logger.warn("useCarrierProfile", "handleDeleteMember failed", { err: error });
      toast.error("Failed to delete team member.");
    } finally {
      setLoading(false);
    }
  };


  // Handle API Call
  const handleInvite = async () => {
      const requestData = {
        user: {
          email: teamMemberEmail,
        },
        team_member_name: teamMemberName,
        team_member_email: teamMemberEmail,
        team_member_position: teamMemberPosition,
        team_member_phonenumber: teamMemberPhone,
        team_member_carrier_id: id, 
        team_member_status: teamMemberStatus === "Available",
        team_member_priority: teamMemberPriority,
        send_invite: sendInvite,
      };
  
      setIsSubmitting(true);
      try {
        await apiClient("/invite-carrier-team-member", "POST", requestData);
        toast.success(
          sendInvite
            ? "Team member created and invite sent successfully!"
            : "Team member created successfully!"
        );
        fetchTeamMembers();
        handleCloseDialog();
      } catch (error) {
        logger.warn("useCarrierProfile", "handleInvite failed", { err: error });
        toast.error(error?.detail || "Failed to invite team member.");
      } finally {
        setIsSubmitting(false);
      }
  };

  // Fetch team members from the API
  const fetchTeamMembers = async () => {
      setLoading(true);
      try {
        const response = await apiClient(`/team-members/${id}`);
        setTeamMembers(response);
      } catch (error) {
        logger.debug("useCarrierProfile", "fetchTeamMembers failed", { err: error });
      } finally {
        setLoading(false);
      }
  };


  //Companies
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true)
  const [openEditCompanyDialog, setOpenEditCompanyDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editCompanyName, setEditCompanyName] = useState("");
  const [editCompanyNAIC, setEditCompanyNAIC] = useState("");
  const [editCompanyLoading, setEditCompanyLoading] = useState(false);
  const [deleteCompanyLoading, setDeleteCompanyLoading] = useState(false);
  const handleOpenCompanyDialog = () => setOpenCompanyDialog(true);
  const handleCloseCompanyDialog = () => setOpenCompanyDialog(false);

  const fetchCompanies = async () => {
    setCompaniesLoading(true);
    try {
      const response = await apiClient(`/carrier-companies/${id}`, 'GET', {}, {});
      setCompanies(response.data);
    } catch (error) {
      logger.warn("useCarrierProfile", "fetchCompanies failed", { err: error });
      toast.error('Failed to load carrier companies.');
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleAddCompany = async () => {
    if (!companyName || !companyNAIC) {
      toast.error("Please fill out all fields.");
      return;
    }
  
    const requestData = {
      carrier_company_name: companyName,
      carrier_company_naic: companyNAIC,
      carrier_id: id, // Assuming `id` is the carrier ID from `useParams()`
    };
  
    setIsSubmitting(true);
    try {
      await apiClient("/companies/", "POST", requestData);
      toast.success("Company added successfully!");
      handleCloseCompanyDialog(); // Close the dialog
      fetchCompanies(); // Refresh the companies list
    } catch (error) {
      logger.warn("useCarrierProfile", "handleAddCompany failed", { err: error });
      toast.error("Failed to add company. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleEditCompanyClick = async (carrier_company_id) => {
    setEditCompanyLoading(true);
    try {
      const response = await apiClient(`/companies/${carrier_company_id}`, "GET");
      setSelectedCompany(response.data);
      setEditCompanyName(response.data.carrier_company_name);
      setEditCompanyNAIC(response.data.carrier_company_naic);
      setEditCompanyLoading(false);
      setOpenEditCompanyDialog(true);
    } catch (error) {
      logger.warn("useCarrierProfile", "handleEditCompanyClick failed", { err: error });
      toast.error("Failed to fetch company details.");
    } finally {
      setEditCompanyLoading(false);
    }
  };

  const handleUpdateCompany = async () => {
    if (!selectedCompany) return;
  
    const updateData = {
      carrier_company_name: editCompanyName,
      carrier_company_naic: editCompanyNAIC,
    };
  
    setIsSubmitting(true);
    try {
      await apiClient(`/companies/${selectedCompany.carrier_company_id}/`, "PATCH", updateData);
      toast.success("Company updated successfully!");
      setOpenEditCompanyDialog(false);
      fetchCompanies(); // Refresh companies list
    } catch (error) {
      logger.warn("useCarrierProfile", "handleUpdateCompany failed", { err: error });
      toast.error("Failed to update company.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCompany = async (carrier_company_id) => {
    if (!window.confirm("Are you sure you want to delete this company?")) return;
    setDeleteCompanyLoading(true);
    setCompaniesLoading(true);
    try {
      await apiClient(`/companies/${carrier_company_id}`, "DELETE");
      toast.success("Company deleted successfully!");
      fetchCompanies(); // Refresh companies list
    } catch (error) {
      logger.warn("useCarrierProfile", "handleDeleteCompany failed", { err: error });
      toast.error("Failed to delete company.");
    } finally {
      setDeleteCompanyLoading(false);
      setCompaniesLoading(false);
    }
  };

  //Commissions
  const [commissionBondType, setCommissionBondType] = useState("");
  const [commissionPercentage, setCommissionPercentage] = useState("");
  const [commissionEffectiveDate, setCommissionEffectiveDate] = useState("");
  const [openCommissionDialog, setOpenCommissionDialog] = useState(false);
  const [isSubmittingCommission, setIsSubmittingCommission] = useState(false);
  const [commissionLoading, setCommissionLoading] = useState(true);
  const [commissionDetailsLoading, setCommissionDetailsLoading] = useState(true);
  const [commissions, setCommissions] = useState([]);
  const [openUpdateCommissionDialog, setOpenUpdateCommissionDialog] = useState(false);
  const [commissionData, setCommissionData] = useState(null);
  const [selectedCommissionId, setSelectedCommissionId] = useState(null);

// Fields for updating commission
const [bondType, setBondType] = useState("");
const [commissionPercent, setCommissionPercent] = useState("");
const [effectiveDate, setEffectiveDate] = useState("");

  const fetchCommissions = async () => {
    setCommissionLoading(true);
    try {
      const response = await apiClient(`/commissions/by-carrier/?commission_carrier=${id}`, "GET");
      const sortedAlphabetically = response.sort((a, b) => {
        const nameA = a.bond_type_display.name.toUpperCase();
        const nameB = b.bond_type_display.name.toUpperCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
    })
      setCommissions(sortedAlphabetically || []);
    } catch (error) {
      logger.warn("useCarrierProfile", "fetchCommissions failed", { err: error });
      toast.error("Failed to load commissions.");
    } finally {
      setCommissionLoading(false);
    }
  };
  

  const handleAddCommission = async () => {
    if (!commissionBondType || !commissionPercentage || !commissionEffectiveDate) {
      toast.error("Please fill out all fields.");
      return;
    }
  
    const requestData = {
      bond_type: commissionBondType,
      commission_percent: Number.parseInt(commissionPercentage, 10),
      effective_date: commissionEffectiveDate,
      commission_carrier: id, // Assuming `id` is the carrier ID from `useParams()`
      commission_carrier_id: id, // Assuming `id` is the carrier ID from `useParams()`
    };
  
    setIsSubmittingCommission(true);
    try {
      await apiClient("/commissions/", "POST", requestData);
      toast.success("Commission added successfully!");
      setOpenCommissionDialog(false); // Close the dialog
      fetchCommissions(); // Refresh the commissions list
    } catch (error) {
      logger.warn("useCarrierProfile", "handleAddCommission failed", { err: error });
      toast.error("Failed to add commission. Please try again.");
    } finally {
      setIsSubmittingCommission(false);
    }
  };

  const handleOpenUpdateCommissionDialog = async (commissionId) => {
    setSelectedCommissionId(commissionId);
    setOpenUpdateCommissionDialog(true);
    fetchCommissionDetails(commissionId);
  };
  
  const fetchCommissionDetails = async (commissionId) => {
    setCommissionDetailsLoading(true);
    try {
      const response = await apiClient(`/commissions/${commissionId}/`, "GET");
      setCommissionData(response);
      setBondType(response.data.bond_type);
      setCommissionPercent(response.data.commission_percent);
      setEffectiveDate(response.data.effective_date);
    } catch (error) {
      logger.warn("useCarrierProfile", "fetchCommissionDetails failed", { err: error });
      toast.error("Failed to fetch commission details.");
    } finally {
      setCommissionDetailsLoading(false);
    }
  };

  const handleCloseUpdateCommissionDialog = () => {
    setOpenUpdateCommissionDialog(false);
    setSelectedCommissionId(null);
    setBondType("");
    setCommissionPercent("");
    setEffectiveDate("");
  };

  const handleUpdateCommission = async () => {
    if (!selectedCommissionId) return;
    setIsSubmitting(true);
    const commissionDetails = commissionData?.data ?? commissionData;
    const updatedData = {
      commission_carrier_id: id,
      bond_type: bondType || commissionDetails?.bond_type_display?.id,
      commission_percent: commissionPercent || commissionDetails?.commission_percent,
      effective_date: effectiveDate || commissionDetails?.effective_date,
    };

    try {
      await apiClient(`/commissions/${selectedCommissionId}/`, "PATCH", updatedData);
      toast.success("Commission updated successfully!");
      handleCloseUpdateCommissionDialog();
      fetchCommissions(); // Refresh the commissions list
    } catch (error) {
      logger.warn("useCarrierProfile", "handleUpdateCommission failed", { err: error });
      toast.error("Failed to update commission.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCommission = async (commissionId) => {
    if (!window.confirm("Are you sure you want to delete this commission?")) return;
  
    try {
      await apiClient(`/commissions/${commissionId}/`, "DELETE");
      toast.success("Commission deleted successfully!");
      fetchCommissions(); // Refresh the commissions list
    } catch (error) {
      logger.warn("useCarrierProfile", "handleDeleteCommission failed", { err: error });
      toast.error("Failed to delete commission.");
    }
  };

  //Limits
  const [carrierLimits, setCarrierLimits] = useState([]);

 // Carrier Info (License Info) CRUD
 const [licenseInfos, setLicenseInfos] = useState([]);
 const [licenseLoading, setLicenseLoading] = useState(true);
 const [openAddLicenseDialog, setOpenAddLicenseDialog] = useState(false);
 const [openEditLicenseDialog, setOpenEditLicenseDialog] = useState(false);
 const [isSubmittingLicense, setIsSubmittingLicense] = useState(false);
 const [newLicenseType, setNewLicenseType] = useState("");
 const [newLicenseValue, setNewLicenseValue] = useState("");
 const [selectedLicense, setSelectedLicense] = useState(null);

 const fetchLicenseInfos = async () => {
   setLicenseLoading(true);
   try {
     const response = await apiClient(`/license-info/by-carrier/${id}/`, "GET");
     const rows = Array.isArray(response) ? response : (response?.data || []);
     setLicenseInfos(rows || []);
   } catch (error) {
     logger.warn("useCarrierProfile", "fetchLicenseInfos failed", { err: error });
     toast.error("Failed to load carrier info.");
   } finally {
     setLicenseLoading(false);
   }
 };

 const handleAddLicenseInfo = async () => {
   if (!newLicenseType || !newLicenseValue) {
     toast.error("Please fill out all fields.");
     return;
   }
   setIsSubmittingLicense(true);
   try {
     await apiClient(`/license-info/`, "POST", {
       bond_carrier_id: Number(id),
       license_info_type: newLicenseType,
       license_info_value: newLicenseValue,
     });
     toast.success("Carrier info added successfully!");
     setOpenAddLicenseDialog(false);
     setNewLicenseType("");
     setNewLicenseValue("");
     fetchLicenseInfos();
   } catch (error) {
     logger.warn("useCarrierProfile", "handleAddLicenseInfo failed", { err: error });
     toast.error("Failed to add carrier info.");
   } finally {
     setIsSubmittingLicense(false);
   }
 };

 const handleOpenEditLicense = (row) => {
   setSelectedLicense(row);
   setNewLicenseType(row.license_info_type || "");
   setNewLicenseValue(row.license_info_value || "");
   setOpenEditLicenseDialog(true);
 };

 const handleUpdateLicenseInfo = async () => {
   if (!selectedLicense) return;
   setIsSubmittingLicense(true);
   try {
     await apiClient(`/license-info/${selectedLicense.license_id}/`, "PATCH", {
       license_info_type: newLicenseType,
       license_info_value: newLicenseValue,
       bond_carrier_id: Number(id),
     });
     toast.success("Carrier info updated successfully!");
     setOpenEditLicenseDialog(false);
     setSelectedLicense(null);
     setNewLicenseType("");
     setNewLicenseValue("");
     fetchLicenseInfos();
   } catch (error) {
     logger.warn("useCarrierProfile", "handleUpdateLicenseInfo failed", { err: error });
     toast.error("Failed to update carrier info.");
   } finally {
     setIsSubmittingLicense(false);
   }
 };

 const handleDeleteLicenseInfo = async (licenseId) => {
   if (!window.confirm("Are you sure you want to delete this carrier info?")) return;
   try {
     await apiClient(`/license-info/${licenseId}/`, "DELETE");
     toast.success("Carrier info deleted successfully!");
     fetchLicenseInfos();
   } catch (error) {
     logger.warn("useCarrierProfile", "handleDeleteLicenseInfo failed", { err: error });
     toast.error("Failed to delete carrier info.");
   }
 };

const fetchCarrierLimits = async () => {
  try {
    const data = await apiClient(`/carrier-limits/${id}/`);
    const sortedAlphabetically = data.data.sort((a, b) => {
      const nameA = a.bond_type_display.type.toUpperCase();
      const nameB = b.bond_type_display.type.toUpperCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;})
    setCarrierLimits(sortedAlphabetically || []); // Adjust to match API response format
  } catch (error) {
    logger.warn("useCarrierProfile", "fetchCarrierLimits failed", { err: error });
  }
};

const createCarrierLimit = async (limitData) => {
  try {
    const response = await apiClient("/limits/", "POST", limitData);
    return response;
  } catch (error) {
    logger.warn("useCarrierProfile", "createCarrierLimit failed", { err: error });
    throw error;
  }
};


const handleAddLimits = async () => {
  if (!limitBondType || !limitAuthorityLimit || !limitEffectiveDate) {
    toast.error("Please fill out all fields.");
    return;
  }

  const requestData = {
    bond_type: limitBondType,
    bond_amount: Number.parseInt(limitAuthorityLimit, 10), // Ensure numeric input
    effective_date: limitEffectiveDate,
    limits_carrier: id, // Carrier ID from `useParams()`
  };

  setIsSubmitting(true);
  try {
    await createCarrierLimit(requestData);
    toast.success("Limit added successfully!");
    setOpenLimitDialog(false); // Close the dialog
    fetchCarrierLimits(); // Refresh the limits list
  } catch (error) {
    logger.warn("useCarrierProfile", "handleAddLimits failed", { err: error });
    toast.error("Failed to add limit. Please try again.");
  } finally {
    setIsSubmitting(false);
  }
};

const fetchLimitDetails = async (limitId) => {
  try {
    const response = await apiClient(`/limits/${limitId}/`, "GET");
    return response.data;
  } catch (error) {
    logger.warn("useCarrierProfile", "fetchLimitDetails failed", { err: error });
    toast.error("Failed to fetch limit details.");
    throw error;
  }
};

const [isEditLimitDialogOpen, setIsEditLimitDialogOpen] = useState(false);
const [editLimitDetails, setEditLimitDetails] = useState({
  bond_amount: "",
  effective_date: "",
  bond_type: "",
});
const [editingLimitId, setEditingLimitId] = useState(null);

const handleEditLimitClick = async (limitId) => {
  try {
    const limit = await fetchLimitDetails(limitId);
    setEditLimitDetails({
      bond_amount: limit.bond_amount,
      effective_date: limit.effective_date,
      bond_type: limit.bond_type,
    });
    setEditingLimitId(limitId);
    setIsEditLimitDialogOpen(true);
  } catch (error) {
    logger.warn("useCarrierProfile", "handleEditLimitClick failed", { err: error });
  }
};


const handleUpdateLimit = async () => {
  if (!editingLimitId) return;

  const requestData = {
    bond_amount: editLimitDetails.bond_amount,
    effective_date: editLimitDetails.effective_date,
    bond_type: editLimitDetails.bond_type,
  };

  setIsSubmitting(true);
  try {
    await apiClient(`/limits/${editingLimitId}/`, "PATCH", requestData);
    toast.success("Limit updated successfully!");
    setIsEditLimitDialogOpen(false);
    fetchCarrierLimits(); // Refresh the list
  } catch (error) {
    logger.warn("useCarrierProfile", "handleUpdateLimit failed", { err: error });
    toast.error("Failed to update limit.");
  } finally {
    setIsSubmitting(false);
  }
};


const handleDeleteLimit = async (limitId) => {
  if (!window.confirm("Are you sure you want to delete this limit?")) return;

  setIsSubmitting(true);
  try {
    await apiClient(`/limits/${limitId}/`, "DELETE");
    toast.success("Limit deleted successfully!");
    fetchCarrierLimits(); // Refresh the list
  } catch (error) {
    logger.warn("useCarrierProfile", "handleDeleteLimit failed", { err: error });
    toast.error("Failed to delete limit.");
  } finally {
    setIsSubmitting(false);
  }
};




  


  //Complete
  const [statesList, setStatesList] = useState([]);
  const [bondsList, setBondsList] = useState([]);
  const fetchBondTypesAndStates = async () => {
    setLoading(true);
    try {
      const response = await apiClient("/bondtypes/", "GET");
      const statesRes = await apiClient(`/states/0/`, "GET");
      setBondsList(response || []);
      setStatesList(Array.isArray(statesRes) ? statesRes : statesRes || []);
    } catch (error) {
      logger.warn("useCarrierProfile", "fetchBondTypesAndStates failed", { err: error });
      toast.error("Failed to load bond types.");
    } finally {
      setLoading(false);
    }
  };

  //Applicable States
  const [states, setStates] = useState([]);
  const fetchCarrierStates = async () => {
    setLoading(true);
    try {
      const response = await apiClient(`/carrier-states/${id}`, "GET"); // Update the endpoint if needed
      setStates(response.data || []); // Assuming the response contains the list of states
    } catch (error) {
      logger.warn("useCarrierProfile", "fetchCarrierStates failed", { err: error });
      toast.error("Failed to fetch states.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddState = async () => {
    if (!stateName) {
      toast.error("Please select a state.");
      return;
    }
  
    const requestData = {
      bond_carrier_id: id, // Carrier ID from `useParams()`
      bond_state_id: stateName, // Selected state ID
    };
  
    setIsSubmitting(true);
    try {
      await apiClient("/carrier-states/", "POST", requestData);
      toast.success("State added successfully!");
      setOpenStateDialog(false);
      fetchCarrierStates(); // Refresh the states list
    } catch (error) {
      logger.warn("useCarrierProfile", "handleAddState failed", { err: error });
      toast.error("Failed to add state.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleDeleteState = async (stateId) => {
    if (!window.confirm("Are you sure you want to delete this state?")) return;
  
    setLoading(true);
    try {
      await apiClient(`/carrier-states/${stateId}/`, "DELETE"); // Update endpoint if needed
      toast.success("State deleted successfully!");
      fetchCarrierStates(); // Refresh the states list
    } catch (error) {
      logger.warn("useCarrierProfile", "handleDeleteState failed", { err: error });
      toast.error("Failed to delete state.");
    } finally {
      setLoading(false);
    }
  };
  
  //Applicanle Bonds
  const [bondsLoading, setBondsLoading] = useState(true);
  const fetchCarrierBonds = async () => {
    setBondsLoading(true);
    try {
      const response = await apiClient(`/carrier-bondtype-list/${id}/`, "GET"); // Update the endpoint if needed
      const sortedAlphabetically = response.data.sort((a, b) => {
        const nameA = a.bond_type.type.toUpperCase();
        const nameB = b.bond_type.type.toUpperCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;})
      setBonds(sortedAlphabetically || []); // Assuming the response contains the list of states
    } catch (error) {
      logger.warn("useCarrierProfile", "fetchCarrierBonds failed", { err: error });
      toast.error("Failed to fetch bonds.");
    } finally {
      setBondsLoading(false);
    }
  };

  const handleAddBond = async () => {
    if (!bondName) {
      toast.error("Please select a state.");
      return;
    }
  
    const requestData = {
      bond_carrier: id, // Carrier ID from `useParams()`
      bond_carrier_id: id, // Carrier ID from `useParams()`
      bond_type: bondName, // Selected state ID
      bond_type_id: bondName, // Selected state ID
      code: bondCode,
    };
  
    setIsSubmitting(true);
    try {
      await apiClient("/carrier-bondtype/", "POST", requestData);
      toast.success("Bond added successfully!");
      setOpenBondDialog(false);
      fetchCarrierBonds(); // Refresh the states list
    } catch (error) {
      logger.warn("useCarrierProfile", "handleAddBond failed", { err: error });
      toast.error("Failed to add Bond.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const [openEditBondDialog, setOpenEditBondDialog] = useState(false);
const [selectedBond, setSelectedBond] = useState(null);
const [updatedBondCode, setUpdatedBondCode] = useState("");

// Open Edit Bond Dialog
const handleEditBondClick = (bond) => {
  setSelectedBond(bond);
  setUpdatedBondCode(bond.code || "");
  setOpenEditBondDialog(true);
};

// Close Edit Bond Dialog
const handleCloseEditBondDialog = () => {
  setOpenEditBondDialog(false);
  setSelectedBond(null);
  setUpdatedBondCode("");
};

// Handle Bond Update API Call
const handleUpdateBond = async () => {
  if (!selectedBond) return;


  const updateData = {
    bond_type_id:selectedBond.bond_type.bond_type_id,
    bond_carrier_id: selectedBond.bond_carrier,
    code: updatedBondCode,
  };

  setIsSubmitting(true);
  try {
    await apiClient(`/carrier-bondtype/${selectedBond.bond_code_id}/`, "PATCH", updateData);
    toast.success("Bond updated successfully!");
    setOpenEditBondDialog(false);
    fetchCarrierBonds(); // Refresh the bonds list
  } catch (error) {
    logger.warn("useCarrierProfile", "handleUpdateBond failed", { err: error });
    toast.error("Failed to update bond.");
  } finally {
    setIsSubmitting(false);
  }
};

// Handle Bond Delete API Call
const handleDeleteBond = async (bondId) => {
  if (!window.confirm("Are you sure you want to delete this bond?")) return;

  setBondsLoading(true);
  try {
    await apiClient(`/carrier-bondtype/${bondId}/`, "DELETE");
    toast.success("Bond Type deleted successfully!");
    fetchCarrierBonds(); // Refresh the bonds list
  } catch (error) {
    logger.warn("useCarrierProfile", "handleDeleteBond failed", { err: error });
    toast.error("Failed to delete bond.");
  } finally {
    setBondsLoading(false);
  }
};



  

  useEffect(() => {
    fetchCarrierDetails();
    fetchBondTypesAndStates();
    fetchTeamMembers();
    fetchCompanies();
    fetchCommissions(); 
    fetchCarrierLimits();
    fetchCarrierStates();
    fetchCarrierBonds();
    fetchLicenseInfos();
    // fetch bond counts by year for graph
    (async () => {
      try {
        const rows = await apiClient(`/bonds/count-by-year/${id}/`, 'GET');
        const dataRows = Array.isArray(rows) ? rows : (rows?.data || []);
        const labels = dataRows.map(r => r.year);
        const data = dataRows.map(r => r.count);
        setBondGraphData({ labels, datasets: [{ label: 'Bond History', backgroundColor: '#1976d2', data }] });
      } catch (err) {
        logger.debug("useCarrierProfile", "bond count by year fetch failed", { err });
        setBondGraphData({ labels: [], datasets: [{ label: 'Bond History', backgroundColor: '#1976d2', data: [] }] });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const [bondName, setBondName] = useState("");
  const [bondCode, setBondCode] = useState("");
  const [bonds, setBonds] = useState([]);
  const [openStateDialog, setOpenStateDialog] = useState(false);
  const [stateName, setStateName] = useState("");

  // Handle dialog open/close
  const handleOpenStateDialog = () => setOpenStateDialog(true);
  const handleCloseStateDialog = () => setOpenStateDialog(false);

  const handleOpenBondDialog = () => setOpenBondDialog(true);
  const handleCloseBondDialog = () => setOpenBondDialog(false);

  // New state variables for company dialog and data
  const [openCompanyDialog, setOpenCompanyDialog] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyNAIC, setCompanyNAIC] = useState("");

  const handleDefaultChange = (naic) => {
    setCompanies((prevCompanies) =>
      prevCompanies.map((company) =>
        company.naic === naic ? { ...company, isDefault: !company.isDefault } : company
      )
     );
   };  

  // State variables for limits
  const [openLimitDialog, setOpenLimitDialog] = useState(false);
  const [limitBondType, setLimitBondType] = useState("");
  const [limitAuthorityLimit, setLimitAuthorityLimit] = useState("");
  const [limitEffectiveDate, setLimitEffectiveDate] = useState("");

  const handleOpenLimitDialog = () => setOpenLimitDialog(true);
  const handleCloseLimitDialog = () => setOpenLimitDialog(false);

  const formatDate = (date) => {
    const d = new Date(date);
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return '';
    return new Intl.NumberFormat('en-US').format(amount);
  };

  const tableCardSx = CARRIER_PROFILE_TABLE_CARD_SX;
  const headerRowSx = CARRIER_PROFILE_HEADER_ROW_SX;
  const paginationSx = CARRIER_PROFILE_PAGINATION_SX;

  return {
    id,
    open,
    setOpen,
    openBondDialog,
    setOpenBondDialog,
    loading,
    setLoading,
    carrier,
    setCarrier,
    tabValue,
    setTabValue,
    bondGraphData,
    setBondGraphData,
    carrierDetails,
    setCarrierDetails,
    isEditingCarrier,
    setIsEditingCarrier,
    isSavingCarrier,
    setIsSavingCarrier,
    fetchCarrierDetails,
    handleSaveCarrier,
    pageTeam,
    setPageTeam,
    rowsPerPageTeam,
    setRowsPerPageTeam,
    handleChangePageTeam,
    handleChangeRowsPerPageTeam,
    pageCompanies,
    setPageCompanies,
    rowsPerPageCompanies,
    setRowsPerPageCompanies,
    handleChangePageCompanies,
    handleChangeRowsPerPageCompanies,
    pageCommissions,
    setPageCommissions,
    rowsPerPageCommissions,
    setRowsPerPageCommissions,
    handleChangePageCommissions,
    handleChangeRowsPerPageCommissions,
    pageLimits,
    setPageLimits,
    rowsPerPageLimits,
    setRowsPerPageLimits,
    handleChangePageLimits,
    handleChangeRowsPerPageLimits,
    pageStates,
    setPageStates,
    rowsPerPageStates,
    setRowsPerPageStates,
    handleChangePageStates,
    handleChangeRowsPerPageStates,
    pageBonds,
    setPageBonds,
    rowsPerPageBonds,
    setRowsPerPageBonds,
    handleChangePageBonds,
    handleChangeRowsPerPageBonds,
    teamMemberName,
    setTeamMemberName,
    teamMemberEmail,
    setTeamMemberEmail,
    teamMemberPhone,
    setTeamMemberPhone,
    teamMemberPosition,
    setTeamMemberPosition,
    teamMemberStatus,
    setTeamMemberStatus,
    teamMemberPriority,
    setTeamMemberPriority,
    isSubmitting,
    setIsSubmitting,
    sendInvite,
    setSendInvite,
    positions,
    statuses,
    priorities,
    teamMembers,
    setTeamMembers,
    handleOpenDialog,
    handleCloseDialog,
    openEditDialog,
    setOpenEditDialog,
    selectedMember,
    setSelectedMember,
    handleEditClick,
    handleCloseEditDialog,
    handleUpdateMember,
    handleDeleteMember,
    handleInvite,
    fetchTeamMembers,
    companies,
    setCompanies,
    companiesLoading,
    setCompaniesLoading,
    openEditCompanyDialog,
    setOpenEditCompanyDialog,
    selectedCompany,
    setSelectedCompany,
    editCompanyName,
    setEditCompanyName,
    editCompanyNAIC,
    setEditCompanyNAIC,
    editCompanyLoading,
    setEditCompanyLoading,
    deleteCompanyLoading,
    setDeleteCompanyLoading,
    handleOpenCompanyDialog,
    handleCloseCompanyDialog,
    fetchCompanies,
    handleAddCompany,
    handleEditCompanyClick,
    handleUpdateCompany,
    handleDeleteCompany,
    commissionBondType,
    setCommissionBondType,
    commissionPercentage,
    setCommissionPercentage,
    commissionEffectiveDate,
    setCommissionEffectiveDate,
    openCommissionDialog,
    setOpenCommissionDialog,
    isSubmittingCommission,
    setIsSubmittingCommission,
    commissionLoading,
    setCommissionLoading,
    commissionDetailsLoading,
    setCommissionDetailsLoading,
    commissions,
    setCommissions,
    openUpdateCommissionDialog,
    setOpenUpdateCommissionDialog,
    commissionData,
    setCommissionData,
    selectedCommissionId,
    setSelectedCommissionId,
    bondType,
    setBondType,
    commissionPercent,
    setCommissionPercent,
    effectiveDate,
    setEffectiveDate,
    fetchCommissions,
    handleAddCommission,
    handleOpenUpdateCommissionDialog,
    fetchCommissionDetails,
    handleCloseUpdateCommissionDialog,
    handleUpdateCommission,
    handleDeleteCommission,
    carrierLimits,
    setCarrierLimits,
    licenseInfos,
    setLicenseInfos,
    licenseLoading,
    setLicenseLoading,
    openAddLicenseDialog,
    setOpenAddLicenseDialog,
    openEditLicenseDialog,
    setOpenEditLicenseDialog,
    isSubmittingLicense,
    setIsSubmittingLicense,
    newLicenseType,
    setNewLicenseType,
    newLicenseValue,
    setNewLicenseValue,
    selectedLicense,
    setSelectedLicense,
    fetchLicenseInfos,
    handleAddLicenseInfo,
    handleOpenEditLicense,
    handleUpdateLicenseInfo,
    handleDeleteLicenseInfo,
    fetchCarrierLimits,
    createCarrierLimit,
    handleAddLimits,
    fetchLimitDetails,
    isEditLimitDialogOpen,
    setIsEditLimitDialogOpen,
    editLimitDetails,
    setEditLimitDetails,
    editingLimitId,
    setEditingLimitId,
    handleEditLimitClick,
    handleUpdateLimit,
    handleDeleteLimit,
    statesList,
    setStatesList,
    bondsList,
    setBondsList,
    fetchBondTypesAndStates,
    states,
    setStates,
    fetchCarrierStates,
    handleAddState,
    handleDeleteState,
    bondsLoading,
    setBondsLoading,
    fetchCarrierBonds,
    handleAddBond,
    openEditBondDialog,
    setOpenEditBondDialog,
    selectedBond,
    setSelectedBond,
    updatedBondCode,
    setUpdatedBondCode,
    handleEditBondClick,
    handleCloseEditBondDialog,
    handleUpdateBond,
    handleDeleteBond,
    bondName,
    setBondName,
    bondCode,
    setBondCode,
    bonds,
    setBonds,
    openStateDialog,
    setOpenStateDialog,
    stateName,
    setStateName,
    handleOpenStateDialog,
    handleCloseStateDialog,
    handleOpenBondDialog,
    handleCloseBondDialog,
    openCompanyDialog,
    setOpenCompanyDialog,
    companyName,
    setCompanyName,
    companyNAIC,
    setCompanyNAIC,
    handleDefaultChange,
    openLimitDialog,
    setOpenLimitDialog,
    limitBondType,
    setLimitBondType,
    limitAuthorityLimit,
    setLimitAuthorityLimit,
    limitEffectiveDate,
    setLimitEffectiveDate,
    handleOpenLimitDialog,
    handleCloseLimitDialog,
    formatDate,
    formatAmount,
    tableCardSx,
    headerRowSx,
    paginationSx
  };
}
